import accounting from 'accounting'
import get from 'lodash/get'
import { extraSeatTotalCents } from 'app/views/plans/seatPricing'

// The plan is from our database, the coupon is from Stripe. Returns the
// discounted price in cents, clamped at 0 — flat-dollar coupons sized
// for an annual price can exceed a monthly price's cents and would
// otherwise drive the displayed amount negative.
export function priceWithDiscount(plan, price, coupon) {
  if (!doesDiscountApplyToPlan(plan, coupon)) return price

  const off = coupon.percentOff ? price * (coupon.percentOff / 100) : coupon.amountOff
  return Math.max(0, price - off)
}

// Format a cents value as a money string with conditional precision —
// "$96" for whole-dollar amounts, "$95.50" for fractional ones. Shared
// across order-summary and seat-picker so a percentage discount that
// produces a non-whole-dollar total renders the actual charge instead
// of clipping to the next dollar down.
export function formatPriceCents(cents) {
  const dollars = cents / 100
  const precision = dollars % 1 === 0 ? 0 : 2
  return accounting.formatMoney(dollars, { precision })
}

// The plan is from our database, the coupon is from Stripe
export function doesDiscountApplyToPlan(plan, coupon) {
  if (coupon.appliesTo) {
    return coupon.appliesTo.products.includes(plan.sku)
  }

  return true
}

export function nextBilledSubscriptionAmount(subscription) {
  if (get(subscription, ['type'], '') === 'stripe') {
    return formatTotalCurrency(get(subscription, ['upcomingInvoice', 'amountDue'], 0) / 100)
  } else {
    return formatTotalCurrency(get(subscription, ['total', 'amount'], 0) / 100)
  }
}

export function nextBilledAmount(discount, price, plan, selectedSeats) {
  if (['once', 'repeating'].includes(get(discount, 'duration', 'once'))) {
    return formatTotalCurrency((get(plan, ['price', 'amount'], 0) + seatMultiplier(plan, selectedSeats)) / 100)
  } else {
    return formatTotalCurrency(price)
  }
}

// Cost in cents of the seats above the plan's base allotment, SKU-aware:
// FA-TEAM uses the graduated schedule, anything else with seat upgrades
// enabled uses the flat seatUpgradePrice × multiple formula. Thin wrapper
// around `extraSeatTotalCents` so callers can keep passing total seats
// (rather than upgrade seats) without doing the subtraction themselves.
export function seatMultiplier(plan, seats) {
  if (!plan) return 0
  const baseSeats = get(plan, 'seats', 0) || 0
  const upgradeSeats = Math.max(seats - baseSeats, 0)
  return extraSeatTotalCents(plan, upgradeSeats)
}

export function getSubscriptionAmount(subscription) {
  if (subscription.type === 'stripe') {
    return get(subscription, ['latestInvoice', 'amountPaid'], 0)
  } else {
    return get(subscription, ['total', 'amount'], 0)
  }
}

export function getCurrentDiscountLengthLabel(discount, plan) {
  // Currently in Stripe the options are 'once', 'forever', 'repeating'
  // https://docs.stripe.com/api/coupons/object#coupon_object-duration
  // The `plan` arg is used to pick the cycle word — "year" for annual,
  // "month" for monthly. Falls back to "year" when plan is missing so
  // legacy callers continue rendering the same label as before.
  const annual = get(plan, ['annual'], true)
  const periodWord = annual ? 'year' : 'month'

  switch (get(discount, 'duration', 'once')) {
    case 'once': {
      return ` for the first ${periodWord}`
    }
    case 'repeating': {
      // For annual plans we convert durationInMonths to whole years
      // (existing behavior). For monthly plans we treat the number of
      // months as the number of billing cycles the discount applies to.
      const months = get(discount, 'durationInMonths', annual ? 12 : 1)
      const cycles = annual ? months / 12 : months
      return ` for the next ${cyclesLabel(cycles, periodWord)}`
    }
    default: {
      return `per ${periodWord}`
    }
  }
}

export function cyclesLabel(count, periodWord) {
  return count > 1 ? `${count} ${periodWord}s` : periodWord
}

export function yearsLabel(years) {
  return cyclesLabel(years, 'year')
}

export function isProratedAmount(subscription) {
  return get(subscription, ['latestInvoice', 'lines', 'data'], []).find((line) => line.proration)
}

export function formatTotalCurrency(amount) {
  let precision = 0

  if (amount % 1 !== 0) {
    precision = 2

    return accounting.formatMoney(amount, { precision: precision }).split('.')
  }

  return [accounting.formatMoney(amount, { precision: precision })]
}

export function discountRepeatingYears(discount) {
  return get(discount, 'durationInMonths', 12) / 12
}
